"use client";

import { type RefObject, useEffect } from "react";

export const FIXED_ACTION_BAR_DRAG_GUARD_ATTR = "data-fixed-action-bar";

export const usePreventFixedActionBarDrag = () => {
  useEffect(() => {
    let isDraggingFixedActionBar = false;

    const handleTouchStart = (event: TouchEvent) => {
      isDraggingFixedActionBar = Boolean(
        (event.target as Element | null)?.closest(
          `[${FIXED_ACTION_BAR_DRAG_GUARD_ATTR}='true']`,
        ),
      );
    };

    const preventFixedElementDrag = (event: TouchEvent) => {
      if (isDraggingFixedActionBar && event.cancelable) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      isDraggingFixedActionBar = false;
    };

    document.addEventListener("touchstart", handleTouchStart, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchmove", preventFixedElementDrag, {
      capture: true,
      passive: false,
    });
    document.addEventListener("touchend", handleTouchEnd, true);
    document.addEventListener("touchcancel", handleTouchEnd, true);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart, true);
      document.removeEventListener("touchmove", preventFixedElementDrag, true);
      document.removeEventListener("touchend", handleTouchEnd, true);
      document.removeEventListener("touchcancel", handleTouchEnd, true);
    };
  }, []);
};

export const usePreventScrollRubberBand = (
  scrollContainerRef: RefObject<HTMLElement | null>,
) => {
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let startY = 0;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;

      startY = event.touches[0].clientY;
    };

    const preventRubberBand = (event: TouchEvent) => {
      if (event.touches.length !== 1 || !event.cancelable) return;

      const currentY = event.touches[0].clientY;
      const deltaY = currentY - startY;
      const isPullingDown = deltaY > 0;
      const isPushingUp = deltaY < 0;
      const isAtTop = scrollContainer.scrollTop <= 0;
      const isAtBottom =
        Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >=
        scrollContainer.scrollHeight;

      if ((isAtTop && isPullingDown) || (isAtBottom && isPushingUp)) {
        event.preventDefault();
      }
    };

    scrollContainer.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    scrollContainer.addEventListener("touchmove", preventRubberBand, {
      passive: false,
    });

    return () => {
      scrollContainer.removeEventListener("touchstart", handleTouchStart);
      scrollContainer.removeEventListener("touchmove", preventRubberBand);
    };
  }, [scrollContainerRef]);
};
